import React from "react";
import { useTranslation } from "react-i18next";
import useConfirm from "../../../../../../hooks/useConfirm";
import { useGetInvoiceSettings } from "./useGetInvoiceSettings";
import useInvoiceSettingsMutations from "./useInvoiceSettingsMutations";

export function useInvoiceSettingsField(clinicId) {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useGetInvoiceSettings(clinicId);
  const { updateSettings, isUpdating, setStartingSequence, isUpdatingSequence } = useInvoiceSettingsMutations(clinicId);
  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    t("ChangeNextInvoiceNumber"),
    t("Yes"),
    t("No"),
  );

  const [scheme, setScheme] = React.useState("NONE");
  const [premisesCode, setPremisesCode] = React.useState("");
  const [nextInvoiceNumber, setNextInvoiceNumber] = React.useState("");
  const [initialized, setInitialized] = React.useState(false);
  const originalRef = React.useRef({ scheme: "NONE", premisesCode: "" });

  React.useEffect(() => {
    if (!initialized && data) {
      const currentScheme = data?.scheme ?? "NONE";
      const currentPremisesCode = data?.premisesCode != null ? String(data.premisesCode) : "";
      setScheme(currentScheme);
      setPremisesCode(currentPremisesCode);
      setNextInvoiceNumber(data?.nextInvoiceNumber != null ? String(data.nextInvoiceNumber) : "");
      originalRef.current = { scheme: currentScheme, premisesCode: currentPremisesCode };
      setInitialized(true);
    }
  }, [data, initialized]);

  /** Skips the network call when nothing changed, so a page-wide save doesn't touch this setting when only something else was edited. */
  async function save() {
    if (scheme === originalRef.current.scheme && premisesCode === originalRef.current.premisesCode) {
      return;
    }
    const result = await updateSettings(scheme, premisesCode === "" ? null : Number(premisesCode));
    if (result) {
      originalRef.current = { scheme, premisesCode };
      refetch();
    }
  }

  async function saveStartingSequence() {
    getMessage(t("ChangeNextInvoiceNumberDescription", { number: nextInvoiceNumber }));
    const confirmed = await confirm();
    if (!confirmed) {
      return false;
    }
    const result = await setStartingSequence(Number(nextInvoiceNumber));
    if (result) {
      refetch();
    }
    return Boolean(result);
  }

  return {
    scheme,
    setScheme,
    premisesCode,
    setPremisesCode,
    nextInvoiceNumber,
    setNextInvoiceNumber,
    save,
    saveStartingSequence,
    isLoading,
    isUpdating,
    isUpdatingSequence,
    ConfirmationDialog,
  };
}
